import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Megaphone, Phone, Globe, BadgeCheck, Crown } from "lucide-react";
import { api, mediaUrl } from "../lib/api";

// Sponsored business ad spot. Rendered on the landing page (all 3 layouts) and
// at the top of the available-jobs list. Rotates through approved ads; shows a
// "your business here" CTA when nobody is advertising yet.
export default function AdSpot({ variant = "classic", placement = "landing", viewerCity = null }) {
    const [ads, setAds] = useState([]);
    const [idx, setIdx] = useState(0);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let dead = false;
        api.get("/ads/public").then(({ data }) => {
            if (dead) return;
            // City targeting: businesses that picked cities are shown only to
            // viewers whose city matches (anonymous visitors see everything).
            const vc = (viewerCity || "").trim().toLowerCase();
            const filtered = (data.ads || []).filter((a) =>
                !a.target_cities?.length || !vc || a.target_cities.some((c) => c.toLowerCase() === vc));
            setAds(filtered);
            setLoaded(true);
        }).catch(() => { if (!dead) setLoaded(true); });
        return () => { dead = true; };
    }, [viewerCity]);

    useEffect(() => {
        if (ads.length < 2) return;
        const id = setInterval(() => setIdx((i) => (i + 1) % ads.length), 10000);
        return () => clearInterval(id);
    }, [ads.length]);

    if (!loaded) return null;
    const ad = ads.length ? ads[idx % ads.length] : null;
    const tier = ad?.tier || "starter";
    const frame =
        tier === "premium"
            ? "border-amber-400/60 shadow-[0_0_35px_-10px_rgba(251,191,36,0.45)]"
            : tier === "featured"
            ? "border-orange-500/45"
            : "border-white/10";
    const TierBadge = tier === "premium"
        ? <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/50 bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-300"><Crown className="h-3 w-3" />Premium</span>
        : tier === "featured"
        ? <span className="inline-flex items-center gap-1 rounded-full border border-orange-500/50 bg-orange-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-orange-300"><BadgeCheck className="h-3 w-3" />Featured</span>
        : null;

    const shape = variant === "operations" ? "rounded-xl" : variant === "editorial" ? "rounded-[1.5rem]" : "rounded-2xl";
    const eyebrow = variant === "operations" ? "// SPONSORED" : variant === "editorial" ? "Sponsored · local business" : "Sponsored";

    const inner = ad ? (
        <>
            {ad.image_url && (
                <div className={`h-28 w-full sm:w-44 shrink-0 overflow-hidden ${variant === "operations" ? "rounded-lg" : "rounded-xl"}`}>
                    <img src={mediaUrl(ad.image_url)} alt={ad.company_name} className="h-full w-full object-cover" data-testid="ad-image" />
                </div>
            )}
            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">{eyebrow}</span>
                    {TierBadge}
                </div>
                <p data-testid="ad-company" className={`mt-1 font-display text-base sm:text-lg font-extrabold tracking-tight ${variant === "editorial" ? "italic" : "uppercase"}`}>{ad.company_name}</p>
                <p className="mt-0.5 line-clamp-2 text-xs text-zinc-400">{ad.description}</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="rounded-full border border-orange-500/30 bg-orange-500/10 px-2.5 py-0.5 text-[10px] font-bold text-orange-300">{ad.company_type}</span>
                    {ad.target_cities.length > 0 && <span className="text-[10px] text-zinc-500">{ad.target_cities.slice(0, 3).join(" · ")}{ad.target_cities.length > 3 ? " +" + (ad.target_cities.length - 3) : ""}</span>}
                </div>
            </div>
            {(ad.website || ad.phone) && (
                <a
                    data-testid="ad-cta"
                    href={ad.website || `tel:${ad.phone}`}
                    target={ad.website ? "_blank" : undefined}
                    rel="noreferrer"
                    className={`flex shrink-0 items-center gap-1.5 self-center bg-gradient-to-r from-orange-500 to-amber-500 px-4 py-2 text-xs font-bold text-zinc-950 transition-[filter,transform] hover:brightness-110 active:scale-95 ${variant === "operations" ? "rounded-lg font-mono uppercase tracking-wide" : "rounded-xl"}`}
                >
                    {ad.website ? <Globe className="h-3.5 w-3.5" /> : <Phone className="h-3.5 w-3.5" />}
                    {ad.website ? "Visit site" : "Call now"}
                </a>
            )}
        </>
    ) : (
        <>
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-orange-500/30 bg-orange-500/10"><Megaphone className="h-5 w-5 text-orange-400" /></span>
            <div className="min-w-0 flex-1">
                <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">{eyebrow}</span>
                <p className={`mt-1 font-display text-base font-extrabold tracking-tight ${variant === "editorial" ? "" : "uppercase"}`}>Your business could be here</p>
                <p className="mt-0.5 text-xs text-zinc-400">Reach every homeowner on FORCE — from $29/mo.</p>
            </div>
            <button
                data-testid="ad-empty-cta"
                onClick={() => window.dispatchEvent(new CustomEvent("force:start-signup", { detail: { role: "business" } }))}
                className={`shrink-0 self-center border border-orange-500/50 bg-orange-500/10 px-4 py-2 text-xs font-bold text-orange-300 transition-colors hover:bg-orange-500/20 ${variant === "operations" ? "rounded-lg font-mono uppercase tracking-wide" : "rounded-xl"}`}
            >
                Advertise here
            </button>
        </>
    );

    return (
        <div data-testid={`ad-spot-${placement}`} className={placement === "jobs" ? "mb-6" : ""}>
            <AnimatePresence mode="wait">
                <motion.div
                    key={ad ? ad.id : "empty"}
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.35 }}
                    data-testid="ad-spot-card"
                    className={`glass flex flex-col sm:flex-row items-center gap-4 border p-4 sm:p-5 min-h-[7rem] ${shape} ${frame}`}
                >
                    {inner}
                </motion.div>
            </AnimatePresence>
            {ads.length > 1 && (
                <div className="mt-2 flex justify-center gap-1.5" data-testid="ad-rotation-dots">
                    {ads.map((a, i) => (
                        <span key={a.id} className={`h-1.5 rounded-full transition-[width,background-color] ${i === idx % ads.length ? "w-4 bg-orange-500" : "w-1.5 bg-zinc-500/40"}`} />
                    ))}
                </div>
            )}
        </div>
    );
}
